<template>
  <!--
    Bring-your-own Mailgun — the Enterprise BYO sending-infrastructure config
    card. Self-gating: the API 403s (plan_gated) for non-entitled tenants, so a
    non-Enterprise workspace sees NOTHING here (no upsell teaser — this is a
    config surface, not a pitch). Rendered only once an entitled snapshot loads.
  -->
  <MusubiSectionCard v-if="entitled" title="Bring your own Mailgun" icon="envelope" class="byo-card">
    <p class="text-muted small byo-card__intro">
      Route this workspace's email through <strong>your own Mailgun account</strong>
      instead of ours. Your account, your sending reputation, your dedicated
      infrastructure. Sends, domain verification, and delivery webhooks for this
      workspace all use your credentials once enabled.
    </p>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <div v-if="loading" class="text-muted small"><CygnusIcon name="spinner" :spin="true" class="me-1" />Loading…</div>

    <form v-else class="byo-form" @submit.prevent="save">
      <!-- Live routing status -->
      <div :class="['byo-status', snapshot.active ? 'byo-status--on' : 'byo-status--off']">
        <CygnusIcon :name="snapshot.active ? 'check-circle' : 'pause'" />
        <span v-if="snapshot.active">Active — your Mailgun account is sending this workspace's mail.</span>
        <span v-else-if="snapshot.has_api_key">Configured but paused — enable the toggle to route through your account.</span>
        <span v-else>Not configured — add your Mailgun API key below to get started.</span>
      </div>

      <!-- Enabled toggle -->
      <label class="byo-toggle">
        <input type="checkbox" v-model="form.enabled">
        Use my Mailgun account for this workspace
      </label>

      <!-- API key -->
      <label class="byo-field">
        <div class="byo-field__head">
          <span class="byo-field__label">Mailgun API key</span>
          <span v-if="snapshot.has_api_key" class="byo-field__hint">set · {{ snapshot.api_key_masked }}</span>
        </div>
        <div class="byo-field__row">
          <input
            v-model="form.api_key"
            type="password"
            class="form-control"
            autocomplete="new-password"
            :placeholder="snapshot.has_api_key ? 'Leave blank to keep current key' : 'key-...'"
          >
          <button v-if="snapshot.has_api_key" type="button" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="clearField('api_key')">Clear</button>
        </div>
      </label>

      <!-- Webhook signing key -->
      <label class="byo-field">
        <div class="byo-field__head">
          <span class="byo-field__label">Webhook signing key</span>
          <span v-if="snapshot.has_webhook_key" class="byo-field__hint">set · {{ snapshot.webhook_key_masked }}</span>
        </div>
        <div class="byo-field__row">
          <input
            v-model="form.webhook_key"
            type="password"
            class="form-control"
            autocomplete="new-password"
            :placeholder="snapshot.has_webhook_key ? 'Leave blank to keep current key' : 'whsec-...'"
          >
          <button v-if="snapshot.has_webhook_key" type="button" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="clearField('webhook_key')">Clear</button>
        </div>
        <p class="byo-field__help">
          Used to verify delivery + inbound webhooks for your domains. Find it in
          Mailgun under Sending ▸ Webhooks ▸ HTTP webhook signing key.
        </p>
      </label>

      <!-- Region -->
      <label class="byo-field">
        <div class="byo-field__head"><span class="byo-field__label">Region</span></div>
        <select v-model="form.region" class="form-select byo-field__region">
          <option value="us">US (api.mailgun.net)</option>
          <option value="eu">EU (api.eu.mailgun.net)</option>
        </select>
        <p class="byo-field__help">Match the region your Mailgun account was created in — it can't be changed there without re-verifying domains.</p>
      </label>

      <!-- Honest responsibility note -->
      <div class="byo-note">
        <CygnusIcon name="info-circle" />
        <span>
          On your own account, <strong>deliverability and warm-up are yours to manage</strong>.
          Any sending domains you add for this workspace are created and verified
          in <strong>your</strong> Mailgun account — verify their DNS records there.
          Volume limits, suppression, and quotas still apply exactly as before.
        </span>
      </div>

      <div class="byo-actions">
        <button type="submit" class="btn btn-primary btn-sm" :disabled="busy">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy" />Save
        </button>
      </div>
    </form>
  </MusubiSectionCard>
</template>

<script>
/**
 * AdminByoMailgun — Enterprise BYO Mailgun config card (bring-your-own sending
 * infrastructure). Reads/writes /api/v1/admin/byo-mailgun; the endpoint gates on
 * the `byo_mailgun` plan feature (plan_gated 403 for non-entitled), so this card
 * simply renders nothing when the GET is refused — no surface for non-Enterprise
 * tenants. Secrets are masked; a blank secret input keeps the current value, and
 * the per-field Clear button removes it (reverting that field to the platform).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";

export default {
  name: 'AdminByoMailgun',
  components: { MusubiSectionCard },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      entitled: false,
      snapshot: {},
      form: { api_key: '', webhook_key: '', region: 'us', enabled: false },
      loading: true,
      busy: false,
      error: null,
      message: '',
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/byo-mailgun');
        const p = res?.data ?? res;
        this.entitled = true;
        this.applySnapshot(p);
      } catch (e) {
        // 403 plan_gated → not entitled → render nothing (no upsell).
        if (e?.response?.status === 403) {
          this.entitled = false;
        } else {
          this.entitled = true;
          this.error = e?.response?.data?.error || e?.message || 'Failed to load BYO Mailgun settings.';
        }
      } finally {
        this.loading = false;
      }
    },
    applySnapshot(p) {
      this.snapshot = p || {};
      this.form.region = this.snapshot.region || 'us';
      this.form.enabled = !!this.snapshot.enabled;
      // Secrets stay blank (masked) — a blank submit keeps the current value.
      this.form.api_key = '';
      this.form.webhook_key = '';
    },
    async save() {
      this.busy = true;
      this.error = null;
      this.message = '';
      const body = { region: this.form.region, enabled: this.form.enabled };
      // Only send secrets the user actually typed (blank = keep current).
      if (this.form.api_key !== '') body.api_key = this.form.api_key;
      if (this.form.webhook_key !== '') body.webhook_key = this.form.webhook_key;
      try {
        const res = await this.api.put('/api/v1/admin/byo-mailgun', body);
        const p = res?.data ?? res;
        if (p?.success) {
          this.message = 'Saved.';
          this.applySnapshot(p);
        } else {
          this.error = p?.error || 'Save failed.';
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.busy = false;
      }
    },
    async clearField(field) {
      this.busy = true;
      this.error = null;
      this.message = '';
      try {
        const res = await this.api.put('/api/v1/admin/byo-mailgun', { [field]: '' });
        const p = res?.data ?? res;
        this.message = 'Cleared.';
        this.applySnapshot(p);
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to clear.';
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.byo-card { margin-top: 18px; }
.byo-card__intro { margin-bottom: 12px; }
.byo-form { display: flex; flex-direction: column; gap: 14px; max-width: 640px; }
.byo-status { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 8px; font-size: 0.88rem; }
.byo-status--on { background: #e8f5e9; color: var(--m-status-success, #1b5e20); }
.byo-status--off { background: var(--m-bg-content, #f4f5f7); color: var(--m-text-tertiary, #666); }
.byo-toggle { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; font-size: 0.9rem; }
.byo-field { display: flex; flex-direction: column; gap: 5px; }
.byo-field__head { display: flex; align-items: baseline; gap: 10px; }
.byo-field__label { font-weight: 600; font-size: 0.85rem; }
.byo-field__hint { font-size: 0.75rem; color: var(--m-text-tertiary, #777); }
.byo-field__row { display: flex; gap: 8px; align-items: center; }
.byo-field__row .form-control { flex: 1; }
.byo-field__region { max-width: 280px; }
.byo-field__help { font-size: 0.76rem; color: var(--m-text-tertiary, #777); margin: 0; }
.byo-note { display: flex; gap: 8px; padding: 10px 12px; border-radius: 8px; background: var(--m-bg-content, #f4f5f7); font-size: 0.8rem; color: var(--m-text-secondary, #555); }
.byo-note i { margin-top: 2px; }
.byo-actions { display: flex; gap: 8px; }
</style>
